// 微信扫码登录的「网页回跳」入口。
//
// 链路：手机确认 → 微信做顶层跳转 → 后端 GET /api/wx/callback?code=&state=
//      → 后端换 code、建/复用用户、签发 session → 302 到 wx.open.redirect-url：
//        https://dtviso.com/?wechat_token=<session>&returnTo=account
//
// 本模块负责最后一步：把 token 从地址栏摘下来（不留在历史记录 / Referer / 截图里），
// 用它调 /v1/auth/me 换回完整会话，再交给 AuthSession。
//
// ⚠ 为什么采摘必须在「模块求值阶段」完成，而不是等 main.js 调函数：
//   router/index.js 里的 new VueRouter({ mode: 'hash' }) 在**构造时**就会读 location，
//   并用 replaceState 把地址栏改写成 #/xxx —— 这一步会把 ?wechat_token= 一并抹掉。
//   ES 模块按 import 顺序求值，所以 main.js 必须把本模块排在 './router' 之前。
import { authApi, authSession } from './runtime'
import { recalledLoginIntent, clearLoginIntent } from './login-return'
import { routeUrl } from './navigation'

// returnTo 由后端拼在 URL 上，不可信；只认这几条路由，杜绝开放重定向。
const RETURN_ROUTES = {
  account: '/account',
  home: '/home',
  works: '/works',
  wallet: '/wallet',
  recharge: '/recharge',
  profiles: '/profiles',
}

// 与 auth-api.js parseSession 对 accessToken 的口径保持一致。
const TOKEN_RE = /^[A-Za-z0-9._~+/-]+=*$/

function capture() {
  if (typeof window === 'undefined' || !window.location) return null
  let params
  try {
    params = new URLSearchParams(window.location.search)
  } catch (error) {
    return null
  }
  // 后端回调失败时不会甩 JSON 给浏览器，而是 302 回来带 login_error=<错误码>。
  // 这里把它搬进 hash 交给登录页展示，地址栏本身不留错误参数。
  const error = params.get('login_error')
  if (error) {
    params.delete('login_error')
    const rest = params.toString()
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (rest ? '?' + rest : '') + '#/login?wechatError=' + encodeURIComponent(error),
    )
    return { error }
  }
  const token = params.get('wechat_token')
  if (!token) return null
  const intended = recalledLoginIntent()
  const path = Object.keys(intended.query).length ? routeUrl(intended.name, intended.query) : RETURN_ROUTES[params.get('returnTo')] || RETURN_ROUTES.account
  params.delete('wechat_token')
  params.delete('returnTo')
  const rest = params.toString()
  window.history.replaceState(
    {},
    '',
    window.location.pathname + (rest ? '?' + rest : '') + '#' + path,
  )
  return { token, path }
}

// 模块求值即执行，必须早于 router 的构造（见文件头注释）。
const captured = capture()

let pending = null

/**
 * 完成「一次性 token → 会话」的兑换。
 * 地址栏里既没有 wechat_token 也没有 login_error 时返回 null；
 * 重复调用复用同一个 Promise，不会重复兑换。
 * 返回体的 route 是失败时应该跳转到的路由（带错误提示查询串）。
 */
export function consumeWechatCallback() {
  if (!captured) return Promise.resolve(null)
  if (captured.error) {
    return Promise.resolve({
      ok: false,
      route: '/login?wechatError=' + encodeURIComponent(captured.error),
      reason: captured.error,
    })
  }
  if (pending) return pending
  pending = (async () => {
    if (!TOKEN_RE.test(captured.token)) {
      return { ok: false, path: captured.path, route: '/login', reason: 'INVALID_TOKEN' }
    }
    try {
      const me = await authApi.me(captured.token)
      authSession.applySession({
        accessToken: captured.token,
        tokenType: 'Bearer',
        user: me.user,
        expiresAt: me.expiresAt,
      })
      clearLoginIntent()
      return { ok: true, path: captured.path, route: captured.path }
    } catch (error) {
      return {
        ok: false,
        path: captured.path,
        route: '/login',
        reason: (error && error.code) || 'EXCHANGE_FAILED',
      }
    }
  })()
  return pending
}
